if (!customElements.get("gwp-controller")) {
  class GwpController extends HTMLElement {
    constructor() {
      super();

      this.variantId = Number(this.dataset.variantId);
      // Rounding matters: a $100 threshold at rate 1.1 converts to 11000.000000000002, which a cart
      // of exactly 11000 integer cents would fail to reach.
      this.thresholdCents = Math.round(
        Number(this.dataset.threshold) *
          Number(window.Shopify.currency.rate || 1) *
          100
      );

      this.pending = false;
      this.queued = null;
      this.disabled = this.readDisabled();
    }

    connectedCallback() {
      // The cartUpdate payload is not consistently a cart — add-to-cart publishes the added line
      // item — so the state is always re-fetched rather than read off the event.
      this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        (state) => this.reconcile({ source: state && state.source })
      );

      this.reconcile({ silent: true });
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }

    async reconcile({ silent = false, source = null } = {}) {
      if (this.disabled || !this.variantId) return;

      // Claimed before the fetch: two cart updates arriving together would otherwise both read a
      // gift-less cart and both add one. The later update is replayed rather than dropped, since
      // the cart it describes may no longer qualify by the time this mutation finishes.
      if (this.pending) {
        this.queued = { silent, source };
        return;
      }

      this.pending = true;

      try {
        let next = { silent, source };
        while (next) {
          this.queued = null;
          await this.reconcileOnce(next);
          next = this.queued;
        }
      } finally {
        this.pending = false;
        this.queued = null;
      }
    }

    async reconcileOnce({ silent, source }) {
      const cart = await window.FoxThemeCartHelpers.getState().catch(
        () => null
      );
      if (!cart) return;

      const giftLine = this.findGiftLine(cart);

      // Tested before anything else looks at the gift's price. Dropping below the threshold is also
      // what stops the merchant's discount zeroing the gift, so a charged line here is the promotion
      // ending normally rather than breaking.
      if (!this.cartQualifies(cart)) {
        if (giftLine) await this.removeGift(giftLine, { silent, source });
        return;
      }

      if (this.disableIfCharged(giftLine)) return;

      if (!giftLine) await this.addGift({ silent, source });
    }

    // Every promotional line is excluded, not just the current gift: a leftover line from a previous
    // gift product could otherwise push a cart over the threshold and earn another one. Ordinary
    // purchases of the gift variant carry no _gwp property and still count.
    cartQualifies(cart) {
      const subtotal = (cart.items || []).reduce(
        (total, item) =>
          item.properties && item.properties._gwp
            ? total
            : total + item.final_line_price,
        0
      );

      return subtotal >= this.thresholdCents;
    }

    findGiftLine(cart) {
      return (cart.items || []).find(
        (item) =>
          item.variant_id === this.variantId &&
          item.properties &&
          item.properties._gwp
      );
    }

    // A gift the merchant's automatic discount is no longer zeroing is not a promotion any more: the
    // discount expired, was misconfigured, or lost out to a non-combinable code. The controller then
    // stops managing the line and remembers that, so the paid product does not come straight back
    // every time the shopper deletes it. Both templates already expose its remove control once the
    // line is charged.
    disableIfCharged(giftLine) {
      if (!giftLine || giftLine.final_line_price === 0) return false;

      this.disabled = true;
      this.rememberDisabled();
      return true;
    }

    async addGift(options) {
      const added = await this.post(
        window.FoxThemeSettings.routes.cart_add_url,
        {
          items: [
            { id: this.variantId, quantity: 1, properties: { _gwp: true } },
          ],
        }
      );

      if (!added.ok) {
        // 422 is how Shopify refuses a variant it will not sell right now. Anything else is
        // transient, and giving up on it permanently would drop the promotion for the rest of
        // the page session even after the network recovers.
        if (added.status === 422) {
          this.disabled = true;
          console.warn(
            "Gift with purchase could not be added to the cart; it may be out of stock."
          );
        }
        return;
      }

      // The gift is committed server-side from here on, so an unrendered failure would leave the
      // page describing a cart that no longer exists, and the next reconciliation would find the
      // gift, match, and never repair it. Hence the second attempt.
      const rendered = await this.refreshAndRender(options);
      if (!rendered) await this.refreshAndRender(options);
    }

    // /cart/add.js answers with the added line item, not the cart. Re-reading through post() returns
    // the cart and its section HTML in one response, so the two cannot describe different carts when
    // another mutation lands in between.
    async refreshAndRender(options) {
      const refreshed = await this.post(
        window.FoxThemeSettings.routes.cart_update_url,
        {}
      );
      if (!refreshed.ok) return false;

      // Same order as reconcileOnce: only a cart that still qualifies can tell a broken promotion
      // apart from one that has simply ended.
      if (this.cartQualifies(refreshed.body)) {
        this.disableIfCharged(this.findGiftLine(refreshed.body));
      }
      this.render(refreshed.body, options);
      return true;
    }

    // Removes by line-item key rather than variant id: the same variant may also sit in the cart as
    // an ordinary paid line, and a variant-keyed update could zero that one instead of the gift.
    async removeGift(giftLine, options) {
      const removed = await this.post(
        window.FoxThemeSettings.routes.cart_change_url,
        { id: giftLine.key, quantity: 0 }
      );

      if (!removed.ok) {
        this.unlockGiftLines();
        return;
      }

      this.render(removed.body, options);
    }

    // Both cart templates hide the remove control on a gift line because the controller owns it, so
    // a failed removal would otherwise strand the shopper with a gift they cannot delete.
    unlockGiftLines() {
      document
        .querySelectorAll("[data-gwp-line] cart-remove-button")
        .forEach((button) => button.classList.remove("hidden"));
    }

    // Resolves to { ok, status, body } instead of collapsing every failure to null, so callers can
    // tell a definitive rejection from a transient one. status is null when the request never
    // reached Shopify.
    async post(url, body) {
      const sections = this.sectionsToRequest();
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Accept: "application/json",
          }),
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          return { ok: false, status: response.status, body: null };
        }

        return { ok: true, status: response.status, body: await response.json() };
      } catch (e) {
        console.error(e);
        return { ok: false, status: null, body: null };
      }
    }

    // The cart surfaces are not the only components that render themselves from this response: the
    // cartUpdate published afterwards also reaches quick-order-list, whose handler reads
    // cart.sections[sectionId] unconditionally. cart:grouped-sections is how the theme's own cart
    // mutations collect those extra section ids (assets/cart.js).
    sectionsToRequest() {
      const grouped = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:grouped-sections", {
          bubbles: true,
          detail: { sections: grouped },
        })
      );

      // Only one of these is on the page: the drawer renders on every template except the cart
      // page, where cart-items takes over.
      const mainCart = document.querySelector("cart-items");
      if (mainCart) {
        return mainCart
          .getSectionsToRender()
          .map((section) => section.section)
          .concat(grouped);
      }

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        return cartDrawer
          .getSectionsToRender()
          .map((section) => section.id)
          .concat(grouped);
      }

      return grouped;
    }

    render(cart, { silent = false, source = null } = {}) {
      const cartDrawer = document.querySelector("cart-drawer");

      // The header badge and cart-page heading are not covered by the section re-render.
      window.FoxThemeCartHelpers.updateCartCount(cart.item_count);

      const focused = this.captureFocus();

      if (silent) {
        // Nothing else will render this one. cartUpdate is what drives CartDrawer and CartItems to
        // re-render, and publishing it on load would pop the drawer open. Rendered directly rather
        // than via cart:refresh, which replaces the whole drawer subtree and leaks the pub-sub
        // subscriptions its components never release.
        const mainCart = document.querySelector("cart-items");
        if (cartDrawer) cartDrawer.renderContents(cart, true, false);
        if (mainCart) mainCart.renderContents(cart);
      } else {
        // Rendering here as well would render every section twice, which in the drawer replaces the
        // recommendation component mid-flight and leaves its first request running uncancelled.
        cart.source = source;
        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, cart);
      }

      this.restoreFocus(focused);

      // Separate from the cartUpdate emit above: assets/cart-goal.js listens for this DOM event,
      // and cart.js and quick-order-list.js dispatch it after their own mutations.
      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { cart } })
      );

      // The one case that still needs the leaky full refresh: renderContents only ever clears the
      // drawer's is-empty class, never sets it, and the cartUpdate subscriber skips an empty cart
      // altogether (assets/cart-drawer.js).
      if (cartDrawer && cart.item_count === 0) {
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    }

    // Both cart surfaces replace their line-item subtree wholesale, which drops focus to <body>.
    // That lands on a keyboard user exactly when a quantity change crosses the threshold, since this
    // render arrives after cart.js has already restored focus from its own response.
    captureFocus() {
      const active = document.activeElement;
      const name = active && active.getAttribute("name");
      if (!name) return null;

      const line = active.closest("[data-cart-item]");
      return { name, key: line && line.dataset.cartItem };
    }

    restoreFocus(focused) {
      if (!focused) return;

      // Keyed on the line item rather than its index: adding or removing the gift renumbers lines.
      const scope = focused.key
        ? document.querySelector(`[data-cart-item="${focused.key}"]`)
        : document;
      const control = scope && scope.querySelector(`[name="${focused.name}"]`);
      if (control) control.focus();
    }

    // sessionStorage keeps a deliberate removal from being undone by the next page load. Access
    // throws in Safari private browsing, and a promotion is not worth breaking the cart over.
    get disabledKey() {
      return `gwp-charged-${this.variantId}`;
    }

    readDisabled() {
      try {
        return sessionStorage.getItem(this.disabledKey) === "1";
      } catch (e) {
        return false;
      }
    }

    rememberDisabled() {
      try {
        sessionStorage.setItem(this.disabledKey, "1");
      } catch (e) {
        // The opt-out lasts for this page instead of the session. Nothing else to recover.
      }
    }
  }

  customElements.define("gwp-controller", GwpController);
}
